/**
 * Tiga tampilan jendela Estimate LRLC sesudah modelnya ada: tabel model tempat
 * metode dipilih, pemilih sumur yang akan ditaksir, dan perbandingan metode
 * atas satu sumur.
 *
 * Dipisah dari `LrlcEstimate.tsx`, yang memegang pelatihan dan pekerjaan
 * latarnya; yang di sini hanya membaca model dan memanggil satu tindakan.
 */

import { useMemo, useState } from "react";
import { api } from "../api";
import { useAsync } from "../hooks";
import { count } from "../lib/plural";
import type { LogModel, LrlcClass } from "../types";
import { Button, Checkbox, Dropdown, Empty, Loading, Notice, Segmented, TextField,
  useToast } from "./ui";

export interface ClassReport {
  labels: number[];
  rows: { code: number; n: number; predicted: number;
          precision: number | null; recall: number | null; f1: number | null }[];
  confusion: number[][];
}

export type LrlcModel = LogModel & {
  metrics: LogModel["metrics"] & {
    blind_classes?: ClassReport;
    usage?: { rows_used?: number; rows_available?: number };
    scope?: { field?: string | null; batch?: number | null };
  };
};

/** Lapangan tunggal yang dipelajari sebuah model, atau null bagi model gabungan. */
export const fieldOfModel = (m: LrlcModel): string | null => m.metrics.scope?.field ?? null;

/**
 * Model lapangan yang berlaku untuk tiap lapangan, bagi satu metode.
 *
 * Yang terbaru menang: melatih ulang Jirak dengan XGBoost menggantikan
 * XGBoost Jirak yang lama tanpa perlu menghapusnya.
 */
export function fieldModelsOf(models: LrlcModel[], kind: string): Map<string, LrlcModel> {
  const out = new Map<string, LrlcModel>();
  const newestFirst = [...models].sort((a, b) => b.created_at.localeCompare(a.created_at));
  for (const m of newestFirst) {
    const field = fieldOfModel(m);
    if (field && m.kind === kind && !out.has(field)) out.set(field, m);
  }
  return out;
}

/**
 * Akurasi sebuah model di sumur buta satu lapangan, dari baris per sumurnya.
 *
 * Dibobot cacah sampel tiap sumur, sehingga sumur panjang berbicara lebih
 * keras daripada sumur pendek — sama dengan cara akurasi butanya dihitung.
 */
export function blindAccuracyIn(model: LrlcModel, wellsOfField: Set<string>) {
  let hit = 0;
  let n = 0;
  for (const row of model.metrics.per_well) {
    if (!row.blind || !wellsOfField.has(row.well) || row.accuracy == null) continue;
    hit += row.accuracy * row.n;
    n += row.n;
  }
  return n ? { accuracy: hit / n, n } : null;
}

export interface FieldWell { id: number; name: string; field?: string }

/** Kode kelas "LRLC — potential": kelas yang paling ditanyakan. */
export const LRLC_POTENTIAL = 2;

/** Nama pendek tiap metode, untuk judul kolom yang sempit. */
const SHORT: Record<string, string> = {
  xgboost: "XGBoost", random_forest: "Random forest", ann: "ANN", svm: "SVM",
  extra_trees: "Extra trees", gradient_boosting: "Boosting", knn: "kNN",
  naive_bayes: "Naive Bayes", mlp: "MLP", ridge: "Ridge",
};

/**
 * Nama kolom tiap model: nama pendek metodenya, ditambah nomor model bila dua
 * model memakai metode yang sama — XGBoost Limau dan XGBoost seluruh
 * lapangan tidak boleh tampil sebagai dua kolom bernama sama.
 */
export function methodNames(columns: { model_id: number; kind: string; kind_label: string }[]) {
  const tally = new Map<string, number>();
  for (const c of columns) tally.set(c.kind, (tally.get(c.kind) ?? 0) + 1);
  return new Map(columns.map((c) => {
    const short = SHORT[c.kind] ?? c.kind_label;
    return [c.model_id, (tally.get(c.kind) ?? 0) > 1 ? `${short} #${c.model_id}` : short];
  }));
}

export const pct = (value: number | null | undefined) =>
  value === null || value === undefined ? "—" : value.toFixed(3);

/** F1 satu kelas dari rapor sumur buta, atau null. */
export const classF1 = (report: ClassReport | undefined, code: number) =>
  report?.rows.find((row) => row.code === code)?.f1 ?? null;

const cell: React.CSSProperties = {
  padding: "4px 8px", textAlign: "right", font: "400 10.5px var(--font-mono)",
  borderBottom: "1px solid var(--line-soft)", whiteSpace: "nowrap",
};
const head: React.CSSProperties = {
  ...cell, font: "600 9.5px var(--font-mono)", color: "var(--ink-5)",
  borderBottom: "1px solid var(--line-strong)", background: "var(--panel)",
  position: "sticky", top: 0, zIndex: 1,
};
const left: React.CSSProperties = { textAlign: "left" };
const note: React.CSSProperties = {
  font: "400 10px/1.6 var(--font-sans)", color: "var(--ink-6)", maxWidth: 820,
};
const tableBox: React.CSSProperties = {
  borderCollapse: "collapse", background: "var(--panel-inset)",
  border: "1px solid var(--line)",
};

export function Swatch({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: color,
                     border: "1px solid var(--line)", flex: "none" }} />
      {label}
    </span>
  );
}

/* ------------------------------------------------------------ Tabel model */

/**
 * Semua model LRLC proyek ini, satu baris tiap model.
 *
 * Dua pilihan yang berbeda di tiap baris, dan keduanya disengaja terpisah:
 * **Use** memilih satu model — metode — yang dipakai menaksir sumur, sedangkan
 * **Compare** memilih beberapa untuk dibandingkan berdampingan. Model yang
 * dipakai tidak harus ikut dibandingkan, dan sebaliknya.
 */
export function ModelTable({ models, selected, onSelect, compared, onCompare }: {
  models: LrlcModel[];
  selected: number | null;
  onSelect: (id: number) => void;
  compared: Set<number>;
  onCompare: (next: Set<number>) => void;
}) {
  if (!models.length) {
    return (
      <Empty>
        No LRLC model yet. Choose the wells, the inputs and a method on the
        left, then train. Train more than one method to compare them.
      </Empty>
    );
  }
  const best = Math.max(...models.map((m) => m.blind_score.value ?? 0));
  return (
    <div style={{ maxHeight: 260, overflow: "auto", border: "1px solid var(--line)",
                  borderRadius: 7 }}>
      <table style={{ ...tableBox, border: "none", width: "100%" }} aria-label="LRLC models">
        <thead>
          <tr>
            <th style={{ ...head, ...left }}>Use</th>
            <th style={{ ...head, ...left }}>Compare</th>
            <th style={{ ...head, ...left }}>Method</th>
            <th style={{ ...head, ...left }}>Model</th>
            <th style={{ ...head, ...left }}>Trained on</th>
            <th style={head}>Blind accuracy</th>
            <th style={head}>Macro F1</th>
            <th style={head}>F1 LRLC potential</th>
            <th style={head}>Wells trained · blind</th>
            <th style={head}>Window</th>
          </tr>
        </thead>
        <tbody>
          {models.map((m) => (
            <tr key={m.id} onClick={() => onSelect(m.id)}
                style={{ cursor: "pointer",
                         background: m.id === selected ? "var(--hover-soft)" : undefined }}>
              <td style={{ ...cell, ...left }}>
                <input type="radio" name="lrlc-model" checked={m.id === selected}
                       onChange={() => onSelect(m.id)}
                       aria-label={`Use ${m.name}`} />
              </td>
              <td style={{ ...cell, ...left }} onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={compared.has(m.id)}
                       aria-label={`Compare ${m.name}`}
                       onChange={(e) => {
                         const next = new Set(compared);
                         e.target.checked ? next.add(m.id) : next.delete(m.id);
                         onCompare(next);
                       }} />
              </td>
              <td style={{ ...cell, ...left, color: "var(--ink-2)" }}>{m.kind_label}</td>
              <td style={{ ...cell, ...left, color: "var(--ink-3)", maxWidth: 280,
                           overflow: "hidden", textOverflow: "ellipsis" }} title={m.name}>
                {m.name}
              </td>
              <td style={{ ...cell, ...left }}>
                {fieldOfModel(m) ?? `several fields (${m.wells_trained.length + m.wells_blind.length} wells)`}
              </td>
              <td style={{ ...cell, color: m.blind_score.value === best ? "var(--accent)" : undefined,
                           fontWeight: m.blind_score.value === best ? 600 : 400 }}>
                {pct(m.blind_score.value)}
              </td>
              <td style={cell}>{pct(m.blind_score.extra)}</td>
              <td style={cell}>{pct(classF1(m.metrics.blind_classes, LRLC_POTENTIAL))}</td>
              <td style={cell}>{m.wells_trained.length} · {m.wells_blind.length}</td>
              <td style={cell}>±{m.half_window}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------ Sumur yang ditaksir */

interface Candidate {
  well: string;
  well_id: number;
  /** "lengkap" bila LRLC_CLASS penuh, "not complete", atau "no curve". */
  state: string;
  estimable: boolean;
  needs: boolean;
  already_estimated: boolean;
  missing_inputs: string[];
}

type Show = "all" | "unclassified" | "estimable";

/**
 * Sumur mana yang ditaksir dengan model yang dipilih.
 *
 * Status tiap sumur dibaca dari server (`candidates`): apakah ia sudah punya
 * LRLC_CLASS dari penggolong, apakah ia membawa seluruh kurva masukan model
 * ini, dan apakah ia sudah pernah ditaksir. Yang tidak membawa masukannya
 * tidak bisa dicentang, dan kurva yang kurang disebut namanya — daftar belanja,
 * bukan penolakan tanpa sebab.
 */
export function EstimateWells({ model, models, wells, running, mayCompute, onEstimate,
  onEstimateByField }: {
  model: LrlcModel | null;
  /** Seluruh model LRLC, tempat model lapangan dicari. */
  models: LrlcModel[];
  wells: FieldWell[];
  running: boolean;
  mayCompute: boolean;
  onEstimate: (ids: number[]) => void;
  /** Menaksir tiap sumur dengan model lapangannya; `fallback` bagi yang tidak punya. */
  onEstimateByField: (ids: number[], fieldModels: number[], fallback: number | null) => void;
}) {
  const [byField, setByField] = useState(false);
  const [useFallback, setUseFallback] = useState(true);
  const candidates = useAsync(
    (signal) => api.get<{ wells: Candidate[] }>(
      `/api/log-models/${model!.id}/candidates/`, undefined, signal),
    [model?.id], { skip: !model });
  const fieldOf = useMemo(() => new Map(wells.map((w) => [w.id, w.field || ""])), [wells]);
  const fields = useMemo(
    () => [...new Set(wells.map((w) => w.field || ""))].filter(Boolean).sort(), [wells]);

  const [field, setField] = useState("");
  const [search, setSearch] = useState("");
  const [show, setShow] = useState<Show>("unclassified");
  const [picked, setPicked] = useState<Set<number>>(new Set());

  if (!model) return <Empty>Pick the model to estimate with in the Models tab first.</Empty>;
  if (candidates.loading) return <Loading label="Reading which wells carry the inputs…" />;
  if (candidates.error) return <Notice tone="error">{candidates.error}</Notice>;

  const rows = (candidates.data?.wells ?? []).filter((row) => {
    if (field && fieldOf.get(row.well_id) !== field) return false;
    if (search && !row.well.toUpperCase().includes(search.trim().toUpperCase())) return false;
    if (show === "unclassified" && !row.needs) return false;
    if (show === "estimable" && !row.estimable) return false;
    return true;
  });
  const shownEstimable = rows.filter((row) => row.estimable);
  const chosen = [...picked];
  /* Model lapangan dengan metode yang sama dengan model yang dipakai. Model
     gabungan yang dipakai menjadi cadangan bagi lapangan tanpa model sendiri;
     model satu lapangan tidak bisa menjadi cadangan lapangan lain. */
  const fieldModels = fieldModelsOf(models, model.kind);
  const fallback = !fieldOfModel(model) && useFallback ? model : null;
  const covered = chosen.filter((id) => fieldModels.has(fieldOf.get(id) || "")).length;
  const method = SHORT[model.kind] ?? model.kind_label;
  const estimate = () => byField
    ? onEstimateByField(chosen, [...fieldModels.values()].map((m) => m.id), fallback?.id ?? null)
    : onEstimate(chosen);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      <div style={{ display: "flex", gap: 9, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ width: 190 }}>
          <Dropdown label="Field" value={field}
                    options={[{ value: "", label: "Every field" },
                              ...fields.map((name) => ({ value: name, label: name }))]}
                    onChange={setField} />
        </div>
        <div style={{ width: 170 }}>
          <TextField label="Well name" value={search} onChange={setSearch}
                     placeholder="e.g. L5A" />
        </div>
        <Segmented value={show} size="sm" onChange={(value) => setShow(value as Show)}
                   options={[
                     { value: "unclassified", label: "Without LRLC_CLASS" },
                     { value: "estimable", label: "Estimable" },
                     { value: "all", label: "All" },
                   ]} />
      </div>

      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ font: "500 10.5px var(--font-sans)", color: "var(--ink-4)" }}>
          Estimate with
        </span>
        <Segmented value={byField ? "field" : "one"} size="sm"
                   onChange={(value) => setByField(value === "field")}
                   options={[
                     { value: "one", label: `This model: ${model.name}`.slice(0, 60) },
                     { value: "field", label: `Each well's field model (${method}, ${fieldModels.size})` },
                   ]} />
        {byField && !fieldOfModel(model) && (
          <label style={{ font: "400 10.5px var(--font-sans)", color: "var(--ink-4)",
                          display: "inline-flex", gap: 5, alignItems: "center" }}>
            <input type="checkbox" checked={useFallback}
                   onChange={(e) => setUseFallback(e.target.checked)} />
            Where a field has no model, use this all-fields model
          </label>
        )}
      </div>
      {byField && fieldModels.size === 0 && (
        <Notice tone="warn">
          No {method} model has been trained on a single field yet. Train with
          "One per field" on the left first.
        </Notice>
      )}

      <div style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap" }}>
        <Button variant="ghost" disabled={!shownEstimable.length}
                onClick={() => setPicked(new Set([...picked,
                  ...shownEstimable.map((row) => row.well_id)]))}>
          Select the {shownEstimable.length} shown
        </Button>
        <Button variant="ghost" disabled={!picked.size} onClick={() => setPicked(new Set())}>
          Clear
        </Button>
        <span style={{ flex: 1 }} />
        <Button
          variant="primary"
          onClick={estimate}
          busy={running}
          disabled={!chosen.length || running || !mayCompute
                    || (byField && fieldModels.size === 0 && !fallback)}
          title={`Write LRLC_CLASS_EST with ${model.kind_label}, in the ESTIMATED set`}
        >
          {byField
            ? `Estimate ${count(chosen.length, "well")} with field models`
            : `Estimate ${count(chosen.length, "well")} with ${method}`}
        </Button>
      </div>

      <div style={{ maxHeight: 380, overflowY: "auto", border: "1px solid var(--line)",
                    borderRadius: 7, padding: "2px 4px" }}>
        {rows.length === 0 ? (
          <Empty>No well matches these filters.</Empty>
        ) : rows.map((row) => (
          <Checkbox
            key={row.well_id}
            checked={picked.has(row.well_id)}
            disabled={!row.estimable}
            label={row.well}
            meta={[
              fieldOf.get(row.well_id) || "",
              row.state === "lengkap" ? "has LRLC_CLASS"
                : row.state === "no curve" ? "no LRLC_CLASS" : "LRLC_CLASS partial",
              row.estimable ? (row.already_estimated ? "estimated before" : "")
                : `lacks ${row.missing_inputs.join(", ")}`,
            ].filter(Boolean).join(" · ")}
            onChange={(on) => {
              const next = new Set(picked);
              on ? next.add(row.well_id) : next.delete(row.well_id);
              setPicked(next);
            }}
          />
        ))}
      </div>
      {byField && chosen.length > 0 && (
        <span style={{ ...note, color: "var(--ink-4)" }}>
          {covered} of {chosen.length} chosen wells lie in a field with its own
          model;{" "}
          {chosen.length - covered === 0 ? "none needs the fallback."
            : fallback ? `${chosen.length - covered} use ${fallback.name}.`
              : `${chosen.length - covered} will be left out.`}
        </span>
      )}
      <span style={note}>
        {count(chosen.length, "well")} chosen. A well estimated
        again gets a new version of its ESTIMATED set; the earlier estimate stays
        in the version before it. LRLC_CLASS from the classifier is never
        touched.
      </span>
    </div>
  );
}

/* ------------------------------------------------------------ Per lapangan */

/**
 * Model tiap lapangan dibandingkan dengan satu model untuk semua lapangan.
 *
 * Yang dibandingkan adalah akurasi di sumur buta **lapangan itu**: model
 * lapangan diuji di sumur buta lapangannya sendiri, dan model gabungan diukur
 * di sumur butanya yang kebetulan berada di lapangan yang sama. Keduanya bukan
 * sumur yang sama — pembagian butanya diundi terpisah — jadi selisih kecil
 * bukan kesimpulan; yang dicari adalah lapangan tempat selisihnya besar.
 */
export function PerFieldView({ models, wells, overall }: {
  models: LrlcModel[];
  wells: FieldWell[];
  /** Model gabungan pembandingnya, atau null. */
  overall: LrlcModel | null;
}) {
  const kinds = [...new Set(models.filter((m) => fieldOfModel(m)).map((m) => m.kind as string))];
  const [kind, setKind] = useState<string | null>(null);
  const shown = kind && kinds.includes(kind) ? kind : kinds[0] ?? null;
  const byField = shown ? fieldModelsOf(models, shown) : new Map<string, LrlcModel>();
  const wellsOf = useMemo(() => {
    const out = new Map<string, Set<string>>();
    for (const w of wells) {
      if (!w.field) continue;
      if (!out.has(w.field)) out.set(w.field, new Set());
      out.get(w.field)!.add(w.name);
    }
    return out;
  }, [wells]);

  if (!kinds.length) {
    return (
      <Empty>
        No model has been trained on a single field yet. On the left, choose
        "Several" or "All fields", set Models to "One per field", and train.
      </Empty>
    );
  }
  const fields = [...byField.keys()].sort();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      <div style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap" }}>
        <Segmented value={shown ?? ""} size="sm" onChange={setKind}
                   options={kinds.map((k) => ({ value: k, label: SHORT[k] ?? k }))} />
        <span style={note}>
          Against: {overall ? `${overall.name} (${pct(overall.blind_score.value)})`
            : "no all-fields model — train one with Models set to \"One for all\""}
        </span>
      </div>
      <div style={{ maxHeight: 420, overflow: "auto", border: "1px solid var(--line)",
                    borderRadius: 7 }}>
        <table style={{ ...tableBox, border: "none", width: "100%" }}
               aria-label="Field models against one model for all">
          <thead>
            <tr>
              <th style={{ ...head, ...left }}>Field</th>
              <th style={head}>Wells</th>
              <th style={head}>Trained · blind</th>
              <th style={head}>Field model, blind accuracy</th>
              <th style={head}>F1 LRLC potential</th>
              <th style={head}>All-fields model, same field</th>
              <th style={head}>Difference</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => {
              const m = byField.get(field)!;
              const own = m.blind_score.value;
              const other = overall ? blindAccuracyIn(overall, wellsOf.get(field) ?? new Set()) : null;
              const diff = own !== null && other ? own - other.accuracy : null;
              return (
                <tr key={field}>
                  <td style={{ ...cell, ...left, color: "var(--ink-2)" }} title={m.name}>{field}</td>
                  <td style={cell}>{wellsOf.get(field)?.size ?? 0}</td>
                  <td style={cell}>{m.wells_trained.length} · {m.wells_blind.length}</td>
                  <td style={cell}>{pct(own)}</td>
                  <td style={cell}>{pct(classF1(m.metrics.blind_classes, LRLC_POTENTIAL))}</td>
                  <td style={cell} title={other ? `${other.n.toLocaleString()} blind samples` : ""}>
                    {other ? pct(other.accuracy) : "no blind well here"}
                  </td>
                  <td style={{ ...cell, color: diff === null ? undefined
                    : diff > 0.005 ? "var(--accent)" : diff < -0.005 ? "var(--rose)" : undefined }}>
                    {diff === null ? "—" : `${diff > 0 ? "+" : ""}${diff.toFixed(3)}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <span style={note}>
        A positive difference means the field's own model beat the all-fields
        model on that field's blind wells. The two are tested on different
        blind wells, drawn separately, so read small differences as a tie. A
        field model trained on a handful of wells can score high on two blind
        wells and still generalise worse; the Trained · blind column says how
        much stands behind each number.
      </span>
    </div>
  );
}

/* --------------------------------------------------- Perbandingan metode */

export interface Band { code: number; top: number; base: number; color: string; label: string }

export interface Trace { md: number[]; values: (number | null)[]; unit: string }

export interface ComparePayload {
  well: string;
  well_id: number;
  depth_range: [number, number] | null;
  truth: Band[] | null;
  context: { GR: Trace | null; RT: Trace | null };
  columns: {
    model_id: number; name: string; kind: string; kind_label: string;
    blind_score: LogModel["blind_score"];
    error?: string;
    bands?: Band[];
    samples?: number;
    thickness?: Record<string, number>;
    versus_truth?: { n: number; accuracy: number | null; f1_macro: number | null;
                     classes: ClassReport };
  }[];
  agreement: (number | null)[][] | null;
}

/**
 * Beberapa metode atas satu sumur, berdampingan.
 *
 * Kolom pertama adalah LRLC_CLASS dari penggolong bila sumur itu punya — itu
 * yang ditiru semua model — lalu satu kolom tiap model. Di bawahnya: seberapa
 * cocok tiap model dengan penggolong, dan seberapa sering model-model itu
 * sepakat satu sama lain. Lapisan tempat mereka berselisih adalah lapisan yang
 * paling perlu dilihat lagi.
 */
export function CompareMethods({ projectId, wells, models, compared, classes, suggested }: {
  projectId: number;
  wells: FieldWell[];
  models: LrlcModel[];
  compared: Set<number>;
  classes: LrlcClass[];
  /** Sumur yang ditawarkan lebih dulu: sumur buta model yang dipakai. */
  suggested: string[];
}) {
  const toast = useToast();
  const fields = useMemo(
    () => [...new Set(wells.map((w) => w.field || ""))].filter(Boolean).sort(), [wells]);
  const [field, setField] = useState("");
  const [search, setSearch] = useState("");
  const [wellId, setWellId] = useState<number | null>(null);
  const [payload, setPayload] = useState<ComparePayload | null>(null);
  const [busy, setBusy] = useState(false);

  const chosenModels = models.filter((m) => compared.has(m.id));
  const blindFirst = useMemo(() => {
    const blind = new Set(suggested);
    return [...wells].sort((a, b) =>
      Number(blind.has(b.name)) - Number(blind.has(a.name)) || a.name.localeCompare(b.name));
  }, [wells, suggested]);
  const offered = blindFirst.filter((w) =>
    (!field || w.field === field)
    && (!search || w.name.toUpperCase().includes(search.trim().toUpperCase()))).slice(0, 300);
  const well = wellId ?? offered[0]?.id ?? null;

  const run = async () => {
    if (well === null) return;
    setBusy(true);
    try {
      setPayload(await api.post<ComparePayload>("/api/log-models/compare-methods/", {
        project: projectId, well, models: chosenModels.map((m) => m.id) }));
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setBusy(false);
    }
  };

  const blind = new Set(suggested);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      <div style={{ display: "flex", gap: 9, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ width: 170 }}>
          <Dropdown label="Field" value={field}
                    options={[{ value: "", label: "Every field" },
                              ...fields.map((name) => ({ value: name, label: name }))]}
                    onChange={(value) => { setField(value); setWellId(null); }} />
        </div>
        <div style={{ width: 150 }}>
          <TextField label="Well name" value={search}
                     onChange={(value) => { setSearch(value); setWellId(null); }} />
        </div>
        <div style={{ width: 230 }}>
          <Dropdown label="Well" value={well === null ? "" : String(well)}
                    options={offered.length
                      ? offered.map((w) => ({ value: String(w.id),
                          label: blind.has(w.name) ? `${w.name} (blind)` : w.name }))
                      : [{ value: "", label: "No well matches" }]}
                    onChange={(value) => setWellId(value ? Number(value) : null)} />
        </div>
        <Button variant="primary" onClick={run} busy={busy}
                disabled={well === null || chosenModels.length === 0 || busy}
                title={chosenModels.length ? undefined
                  : "Tick Compare on at least one model in the Models tab"}>
          Compare {count(chosenModels.length, "method")}
        </Button>
      </div>
      {chosenModels.length === 0 && (
        <Notice tone="info">
          Tick "Compare" on the models to set side by side in the Models tab —
          one per method is the usual choice.
        </Notice>
      )}
      <span style={note}>
        Wells marked "blind" were held out when the chosen model was trained,
        so what it draws there is a true test. On a training well the model has
        seen the answer.
      </span>
      {payload && <CompareView payload={payload} classes={classes} />}
    </div>
  );
}

export const TRACK_H = 620;

function CompareView({ payload, classes }: { payload: ComparePayload; classes: LrlcClass[] }) {
  const range = payload.depth_range;
  const [top, setTop] = useState<number | null>(null);
  const [base, setBase] = useState<number | null>(null);
  if (!range) {
    return <Notice tone="warn">Not one of these models could estimate {payload.well}.</Notice>;
  }
  const t = top ?? range[0];
  const b = base ?? range[1];
  const y = (md: number) => ((md - t) / Math.max(1e-6, b - t)) * TRACK_H;

  const names = methodNames(payload.columns);
  const columns = [
    ...(payload.truth ? [{ key: "truth", title: "Classifier", sub: "LRLC_CLASS",
                           bands: payload.truth }] : []),
    ...payload.columns.map((c) => ({ key: String(c.model_id), title: names.get(c.model_id)!,
                                     sub: pct(c.blind_score.value), bands: c.bands ?? [],
                                     error: c.error })),
  ];
  const ticks = niceTicks(t, b);
  const colorOf = (code: number) => classes.find((c) => c.code === code)?.color ?? "#999";
  const labelOf = (code: number) => classes.find((c) => c.code === code)?.label ?? String(code);
  const present = new Set<number>();
  for (const column of columns) for (const band of column.bands) present.add(band.code);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
      <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap",
                    font: "400 10px var(--font-mono)", color: "var(--ink-5)" }}>
        <span style={{ color: "var(--ink-2)", fontWeight: 600 }}>{payload.well}</span>
        <label>Top <input type="number" value={Math.round(t)} style={{ width: 70 }}
                          onChange={(e) => setTop(Number(e.target.value))} /></label>
        <label>Base <input type="number" value={Math.round(b)} style={{ width: 70 }}
                           onChange={(e) => setBase(Number(e.target.value))} /></label>
        <button type="button" onClick={() => { setTop(null); setBase(null); }}
                style={{ font: "inherit", cursor: "pointer" }}>Whole well</button>
        {[...present].sort((p, q) => p - q).map((code) => (
          <Swatch key={code} color={colorOf(code)} label={labelOf(code)} />
        ))}
      </div>

      <div style={{ display: "flex", gap: 4, alignItems: "flex-start", overflowX: "auto" }}
           aria-label="Method comparison sheet">
        <Track title="MD m" width={46}>
          {ticks.map((tick) => (
            <text key={tick} x={42} y={y(tick) + 3} textAnchor="end"
                  style={{ font: "9px var(--font-mono)", fill: "var(--ink-5)" }}>{tick}</text>
          ))}
        </Track>
        {payload.context.GR && (
          <Track title="GR" sub={payload.context.GR.unit || "API"} width={90}>
            <CurvePath trace={payload.context.GR} y={y} top={t} base={b} min={0} max={150}
                       log={false} width={90} color="var(--green, #3a8a3a)" />
          </Track>
        )}
        {payload.context.RT && (
          <Track title="RT" sub="0.2–200 log" width={90}>
            <CurvePath trace={payload.context.RT} y={y} top={t} base={b} min={0.2} max={200}
                       log width={90} color="var(--rose, #b33)" />
          </Track>
        )}
        {columns.map((column) => (
          <Track key={column.key} title={column.title} sub={column.sub} width={78}>
            {column.bands
              .filter((band) => band.base >= t && band.top <= b)
              .map((band, i) => {
                const y0 = Math.max(0, y(band.top));
                const y1 = Math.min(TRACK_H, y(band.base));
                return (
                  <rect key={i} x={0} width={78} y={y0} height={Math.max(0.6, y1 - y0)}
                        fill={band.color}>
                    <title>{`${band.label} · ${band.top.toFixed(1)}–${band.base.toFixed(1)} m`}</title>
                  </rect>
                );
              })}
            {"error" in column && column.error ? (
              <text x={4} y={20} style={{ font: "9px var(--font-sans)", fill: "var(--rose)" }}>
                cannot estimate
              </text>
            ) : null}
          </Track>
        ))}
      </div>

      <CompareTables payload={payload} labelOf={labelOf} names={names} />
    </div>
  );
}

export function Track({ title, sub, width, height = TRACK_H, children }: {
  title: string; sub?: string; width: number; height?: number; children: React.ReactNode;
}) {
  return (
    <div style={{ flex: "none", width }}>
      <div style={{ height: 30, font: "600 9.5px var(--font-mono)", color: "var(--ink-3)",
                    textAlign: "center", overflow: "hidden", whiteSpace: "nowrap",
                    textOverflow: "ellipsis" }} title={title}>
        {title}
        <div style={{ font: "400 9px var(--font-mono)", color: "var(--ink-6)" }}>{sub}</div>
      </div>
      <svg width={width} height={height} style={{ display: "block", background: "var(--panel-inset)",
                                                  border: "1px solid var(--line)" }}>
        {children}
      </svg>
    </div>
  );
}

export function CurvePath({ trace, y, top, base, min, max, log, width, color }: {
  trace: Trace; y: (md: number) => number; top: number; base: number;
  min: number; max: number; log: boolean; width: number; color: string;
}) {
  const x = (v: number) => {
    const f = log ? (Math.log10(Math.max(v, 1e-6)) - Math.log10(min))
        / (Math.log10(max) - Math.log10(min)) : (v - min) / (max - min);
    return Math.max(0, Math.min(1, f)) * width;
  };
  let d = "";
  let pen = false;
  trace.md.forEach((md, i) => {
    const v = trace.values[i];
    if (md < top || md > base || v === null) { pen = false; return; }
    d += `${pen ? "L" : "M"}${x(v).toFixed(1)},${y(md).toFixed(1)}`;
    pen = true;
  });
  return <path d={d} fill="none" stroke={color} strokeWidth={1} />;
}

/** Enam sampai sepuluh angka bulat di antara `top` dan `base`. */
export function niceTicks(top: number, base: number): number[] {
  const span = Math.max(1, base - top);
  const raw = span / 8;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((k) => k * mag).find((s) => s >= raw) ?? raw;
  const out: number[] = [];
  for (let v = Math.ceil(top / step) * step; v <= base; v += step) out.push(Math.round(v));
  return out;
}

function CompareTables({ payload, labelOf, names }: {
  payload: ComparePayload; labelOf: (code: number) => string; names: Map<number, string>;
}) {
  const codes = [...new Set(payload.columns.flatMap((c) => Object.keys(c.thickness ?? {})))]
    .map(Number).sort((a, b) => a - b);
  return (
    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-start" }}>
      <table style={tableBox} aria-label="Methods on this well">
        <thead>
          <tr>
            <th style={{ ...head, ...left }}>Method</th>
            <th style={head}>Blind accuracy (training)</th>
            {payload.truth && <th style={head}>Match with classifier</th>}
            {payload.truth && <th style={head}>Macro F1 here</th>}
            {payload.truth && <th style={head}>F1 LRLC potential here</th>}
            {codes.map((code) => <th key={code} style={head}>{labelOf(code)} m</th>)}
          </tr>
        </thead>
        <tbody>
          {payload.columns.map((c) => (
            <tr key={c.model_id}>
              <td style={{ ...cell, ...left, color: "var(--ink-2)" }} title={c.name}>
                {names.get(c.model_id)}
              </td>
              <td style={cell}>{pct(c.blind_score.value)}</td>
              {payload.truth && <td style={{ ...cell, color: "var(--accent)" }}>
                {pct(c.versus_truth?.accuracy)}</td>}
              {payload.truth && <td style={cell}>{pct(c.versus_truth?.f1_macro)}</td>}
              {payload.truth && <td style={cell}>
                {pct(classF1(c.versus_truth?.classes, LRLC_POTENTIAL))}</td>}
              {codes.map((code) => (
                <td key={code} style={cell}>
                  {c.thickness?.[String(code)]?.toFixed(1) ?? (c.error ? "—" : "0.0")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {payload.agreement && payload.columns.length > 1 && (
        <table style={tableBox} aria-label="Agreement between methods">
          <thead>
            <tr>
              <th style={{ ...head, ...left }}>Agreement</th>
              {payload.columns.map((c) => (
                <th key={c.model_id} style={head}>{names.get(c.model_id)}</th>))}
            </tr>
          </thead>
          <tbody>
            {payload.columns.map((c, i) => (
              <tr key={c.model_id}>
                <td style={{ ...cell, ...left }}>{names.get(c.model_id)}</td>
                {payload.agreement![i].map((value, j) => (
                  <td key={j} style={{ ...cell, color: i === j ? "var(--ink-6)" : undefined }}>
                    {pct(value)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <span style={note}>
        "Match with classifier" is the share of samples where the method gives
        the class the LRLC classifier gave; it is only shown on a well the
        classifier has run on. Agreement is the share of samples two methods
        call the same class. Thickness is samples times the depth step.
      </span>
    </div>
  );
}
